import {
  ACCESS_LEGITIMACY_PAGE_SIZE,
  ACCESS_SCOPE_STALE,
  DAYS_FILTER_OPTIONS,
  type AccessLegitimacySortDir,
  type TimeFrameParam,
} from '@dawn/feature-config';
import {
  AccessLegitimacyBody,
  BRAND_LABELS,
  TagChip,
  WidgetCollectingState,
  WidgetErrorState,
  WidgetLoadingState,
} from '@dawn/ui';
import { isAxiosError } from 'axios';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAccessLegitimacyQuery } from '@/hooks/queries/use-access-legitimacy-query';
import { formatEventDateTime } from '@/lib/access-legitimacy-datetime';
import { deriveFilterOptions } from '@/lib/access-legitimacy-filter-options';
import { buildAccessLegitimacyParams } from '@/lib/access-legitimacy-params';
import {
  linkKey,
  parseAccessDiscoveryLink,
  serializeAccessDiscoveryLink,
  stripScopeAxis,
  type AccessDiscoveryLink,
  type AccessDiscoveryLinkAxis,
} from '@/lib/access-legitimacy-scope';
import { createDebounced } from '@/lib/debounce';

const WIDGET_TITLE = 'Access Discovery';
const SEARCH_DEBOUNCE_MS = 750;
const NO_USER_TYPES: readonly string[] = [];

const STALE_SCOPE_MESSAGE =
  'Access Discovery was rebuilt since this view was opened. Reload the chart in a few minutes and choose the cluster again.';

const isStaleScopeError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response?.status === 409 &&
  (error.response.data as { errorCode?: unknown } | undefined)?.errorCode === ACCESS_SCOPE_STALE;

/**
 * AccessDiscoveryPage — the events-grid page container (design v6 §3.3). Owns
 * filter / sort / page / selected-event state and the request lifecycle, builds
 * the typed request params, runs the TanStack query, derives the filter options
 * from the live envelope, and renders the Loading / Error / Empty / populated
 * state machine. `AccessLegitimacyBody` is a pure controlled renderer beneath it.
 */
export function AccessDiscoveryPage() {
  // 0-indexed page (drives `offset`); the body/grid use a 1-indexed page.
  const [page, setPage] = useState(0);
  const [sortDir, setSortDir] = useState<AccessLegitimacySortDir>('desc');
  const [selectedFileTypes, setSelectedFileTypes] = useState<readonly string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // The drill-down link lives in the URL: the Access Discovery node scope
  // (TDEV-1046) plus the Overview time range and widget service (TDEV-1114)
  // and the sensitivity pill (TDEV-1132), the user-type pill and one Overview
  // file or identity (TDEV-1116). "Show Access Events" lands here with it, each
  // chip strips its own axis, and a pill writes its value back. A link change is
  // a filter change, so it resets to page 1 too.
  const [searchParams, setSearchParams] = useSearchParams();
  const link = useMemo(() => parseAccessDiscoveryLink(searchParams), [searchParams]);
  const scope = link.scope ?? {};
  const currentLinkKey = linkKey(link);
  const selectedSensitivity = link.sensitivity ?? 'all';
  const selectedUserTypes = link.userTypes ?? NO_USER_TYPES;
  useEffect(() => {
    setPage(0);
  }, [currentLinkKey]);
  // Replace, not push (plan D6): Back leaves the grid rather than re-adding a chip.
  const removeScopeAxis = (axis: AccessDiscoveryLinkAxis) => {
    setSearchParams(stripScopeAxis(searchParams, axis), { replace: true });
  };
  const updateLink = (patch: Partial<AccessDiscoveryLink>) => {
    setSearchParams(serializeAccessDiscoveryLink({ ...link, ...patch }), { replace: true });
  };

  // Debounce the search term into the query key so the grid does not thrash a
  // request per keystroke (ADR 0001, ~750 ms). Reuses the shared factory.
  const debouncedSetSearch = useMemo(
    () => createDebounced((value: string) => setDebouncedSearch(value), SEARCH_DEBOUNCE_MS),
    []
  );
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  // Any filter / search / sort change resets to page 1 (PRD story 17).
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setPage(0);
    debouncedSetSearch(value);
  };
  const handleSensitivityChange = (value: string) => {
    updateLink({ sensitivity: value === 'all' ? undefined : value });
    setPage(0);
  };
  const handleUserTypesChange = (value: readonly string[]) => {
    updateLink({ userTypes: value.length > 0 ? [...value] : undefined });
    setPage(0);
  };
  const handleFileTypesChange = (value: readonly string[]) => {
    setSelectedFileTypes(value);
    setPage(0);
  };
  const handleSortDirChange = (value: AccessLegitimacySortDir) => {
    setSortDir(value);
    setPage(0);
  };

  const params = useMemo(
    () =>
      buildAccessLegitimacyParams({
        page,
        sortDir,
        selectedSensitivity,
        selectedUserTypes,
        selectedFileTypes,
        debouncedSearch,
        scope: link.scope,
        timeFrame: link.timeFrame,
        service: link.service,
        file: link.file,
        principal: link.principal,
        asOf: link.asOf,
        analysedOnly: link.analysedOnly,
      }),
    [
      page,
      sortDir,
      selectedSensitivity,
      selectedUserTypes,
      selectedFileTypes,
      debouncedSearch,
      link,
    ]
  );

  const { data, error, isLoading, isError, isFetching, refetch } = useAccessLegitimacyQuery(params);

  const filtersActive =
    selectedSensitivity !== 'all' ||
    selectedUserTypes.length > 0 ||
    selectedFileTypes.length > 0 ||
    debouncedSearch.trim().length > 0 ||
    // The chart cutoff anchors the window and the card's population follows it; neither is a filter.
    linkKey({ ...link, asOf: undefined, analysedOnly: undefined }).length > 0;

  // --- State machine -------------------------------------------------------

  if (isLoading) {
    return (
      <Shell>
        <WidgetLoadingState />
      </Shell>
    );
  }

  if (isError || data === undefined) {
    // A stale scope never succeeds on retry: the ids name the old tree.
    return (
      <Shell>
        {isStaleScopeError(error) ? (
          <WidgetErrorState message={STALE_SCOPE_MESSAGE} />
        ) : (
          <WidgetErrorState onRetry={() => void refetch()} />
        )}
      </Shell>
    );
  }

  // True dataset-empty (no filters / search) → standalone empty state.
  if (data.total === 0 && !filtersActive) {
    return (
      <Shell>
        <WidgetCollectingState title={WIDGET_TITLE} primaryLabel="No events" />
      </Shell>
    );
  }

  const options = deriveFilterOptions(data);
  const pageCount = Math.max(1, Math.ceil(data.total / ACCESS_LEGITIMACY_PAGE_SIZE));

  // Pill-driven params ride in the link too, but only these axes draw a chip.
  const scopeChips =
    scope.identity ||
    scope.data ||
    link.timeFrame ||
    link.service ||
    link.file ||
    link.principal ? (
      <div className="flex flex-wrap items-center gap-2" data-testid="access-legitimacy-scope">
        {scope.identity ? (
          <ScopeChip
            axis="identity"
            label={scope.identity.label}
            onRemove={() => removeScopeAxis('identity')}
          />
        ) : null}
        {scope.data ? (
          <ScopeChip
            axis="data"
            label={scope.data.label}
            onRemove={() => removeScopeAxis('data')}
          />
        ) : null}
        {link.timeFrame ? (
          <ScopeChip
            axis="timeFrame"
            label={timeFrameLabel(link.timeFrame)}
            onRemove={() => removeScopeAxis('timeFrame')}
          />
        ) : null}
        {link.service ? (
          <ScopeChip
            axis="service"
            label={BRAND_LABELS[link.service]}
            onRemove={() => removeScopeAxis('service')}
          />
        ) : null}
        {link.file ? (
          <ScopeChip
            axis="file"
            label={`File: ${link.file.label}`}
            onRemove={() => removeScopeAxis('file')}
          />
        ) : null}
        {link.principal ? (
          <ScopeChip
            axis="principal"
            label={`Identity: ${link.principal.label}`}
            onRemove={() => removeScopeAxis('principal')}
          />
        ) : null}
      </div>
    ) : undefined;

  return (
    // `max-h-full` (not `h-full`) caps the card at the layout's content slot so
    // the grid scrolls internally once a page overflows, while a short page still
    // renders exactly as before instead of stranding the pager at the bottom.
    <div className="flex max-h-full flex-col overflow-hidden rounded-[4px] bg-white">
      <AccessLegitimacyBody
        rows={data.rows}
        total={data.total}
        files={data.files}
        isRefetching={isFetching}
        sensitivityOptions={options.sensitivityOptions}
        userTypeOptions={options.userTypeOptions}
        fileTypeOptions={options.fileTypeOptions}
        selectedSensitivity={selectedSensitivity}
        onSensitivityChange={handleSensitivityChange}
        selectedUserTypes={selectedUserTypes}
        onUserTypesChange={handleUserTypesChange}
        selectedFileTypes={selectedFileTypes}
        onFileTypesChange={handleFileTypesChange}
        searchQuery={searchInput}
        onSearchChange={handleSearchChange}
        sortDir={sortDir}
        onSortDirChange={handleSortDirChange}
        page={page + 1}
        pageCount={pageCount}
        onPageChange={(next) => setPage(next - 1)}
        selectedEventId={selectedEventId}
        onSelectEvent={setSelectedEventId}
        formatDateTime={formatEventDateTime}
        scopeChips={scopeChips}
      />
    </div>
  );
}

/** The Overview's own label for a time range, e.g. "Last 7 Days". */
function timeFrameLabel(timeFrame: TimeFrameParam): string {
  return (
    DAYS_FILTER_OPTIONS.find((option) => String(option.value) === timeFrame)?.label ?? timeFrame
  );
}

/** One removable scope chip; the whole chip is the remove button. */
function ScopeChip({
  axis,
  label,
  onRemove,
}: {
  axis: AccessDiscoveryLinkAxis;
  label: string;
  onRemove: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label={`Remove filter ${label}`}
      data-testid={`access-legitimacy-scope-${axis}`}
      className="rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-dawn-orange"
    >
      <TagChip state="subtle" trailingIcon={<X aria-hidden="true" className="h-3 w-3" />}>
        {label}
      </TagChip>
    </button>
  );
}

/**
 * White card chrome wrapping the non-populated state branches (loading / error /
 * empty). The page title lives in the app-header chrome, so the card carries no
 * heading of its own.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-[4px] bg-white">
      <div className="px-5 pb-5 pt-5">{children}</div>
    </div>
  );
}
